import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { commentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { MessageSquare, Trash2, Send } from 'lucide-react';

export default function CommentsSection({ entityType, entityId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await commentsAPI.list(entityType, entityId);
      setComments(res.data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await commentsAPI.create({ entity_type: entityType, entity_id: entityId, text: newComment.trim() });
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentsAPI.delete(commentId);
      fetchComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const roleColor = (role) => {
    if (role === 'admin') return 'bg-red-100 text-red-700';
    if (role === 'staff') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4" data-testid={`comments-${entityType}-${entityId}`}>
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-medium">Comments ({comments.length})</h4>
      </div>

      {/* Comment input */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="w-full h-9 px-3 pr-10 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="comment-input"
          />
        </div>
        <Button type="submit" size="sm" disabled={submitting || !newComment.trim()} className="h-9" data-testid="post-comment-btn">
          <Send className="h-3.5 w-3.5" />
        </Button>
      </form>

      {/* Comments list */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {comments.map(c => (
            <div key={c.comment_id} className="group flex gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`comment-${c.comment_id}`}>
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium shrink-0">
                {(c.user_name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium">{c.user_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleColor(c.user_role)}`}>{c.user_role}</span>
                  <span className="text-xs text-muted-foreground">{formatTime(c.created_at)}</span>
                </div>
                <p className="text-sm text-foreground/80">{c.text}</p>
              </div>
              {(c.user_id === user?.user_id || user?.role === 'admin') && (
                <Button
                  variant="ghost" size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive shrink-0"
                  onClick={() => handleDelete(c.comment_id)}
                  data-testid={`delete-comment-${c.comment_id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
